import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ClientCombobox } from '@/components/shared/ClientCombobox';
import { AccountCombobox } from '@/components/shared/AccountCombobox';
import { Client, EfromClient } from '@/services/client';
import { streamingServiceApi } from '@/services/streaming-service';
import {
  streamingAccountService,
  EStreamingAccountStatus,
  ESlotStatus,
} from '@/services/streaming-account';
import { comboApi, EComboStatus } from '@/services/combo';
import {
  subscriptionService,
  CreateSubscriptionRequest,
  ESubType,
  ESubscriptionType,
  SubscriptionItemRequest,
} from '@/services/subscription';
import { EPaymentMethod, EPaymentStatus } from '@/services/payment';
import {
  Loader2,
  Calendar,
  User,
  Wallet,
  CircleCheck,
  UserPlus,
  Package,
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ComboItemState {
  serviceId: number;
  accountId: number | undefined;
  slotId: number | undefined;
  pricePlanId: number | undefined;
  subscriptionType: ESubscriptionType;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CreateSubscriptionModal({ open, onOpenChange }: Props) {
  const queryClient = useQueryClient();

  // --- Subscription type ---
  const [subType, setSubType] = useState<ESubType>(ESubType.SINGLE);

  // --- Client ---
  const [clientMode, setClientMode] = useState<'existing' | 'new'>('existing');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientId, setClientId] = useState<number | undefined>();
  const [newClientData, setNewClientData] = useState({
    name: '',
    phone: '',
    from: EfromClient.WEB,
  });

  // --- SINGLE ---
  const [singleServiceId, setSingleServiceId] = useState<number | undefined>();
  const [singleAccountId, setSingleAccountId] = useState<number | undefined>();
  const [singleSubType, setSingleSubType] = useState<ESubscriptionType>(ESubscriptionType.SLOT);
  const [singleSlotId, setSingleSlotId] = useState<number | undefined>();
  const [singlePricePlanId, setSinglePricePlanId] = useState<number | undefined>();

  // --- COMBO ---
  const [selectedComboId, setSelectedComboId] = useState<number | undefined>();
  const [comboItems, setComboItems] = useState<Record<number, ComboItemState>>({});

  // --- Common ---
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState<EPaymentMethod>(EPaymentMethod.TRANSFER);
  const [paymentStatus, setPaymentStatus] = useState<EPaymentStatus>(EPaymentStatus.COMPLETED);

  // ─── Reset ──────────────────────────────────────────────────────────────────

  const handleClose = () => {
    setSubType(ESubType.SINGLE);
    setClientMode('existing');
    setSelectedClient(null);
    setClientId(undefined);
    setNewClientData({ name: '', phone: '', from: EfromClient.WEB });
    setSingleServiceId(undefined);
    setSingleAccountId(undefined);
    setSingleSubType(ESubscriptionType.SLOT);
    setSingleSlotId(undefined);
    setSinglePricePlanId(undefined);
    setSelectedComboId(undefined);
    setComboItems({});
    setStartDate(new Date().toISOString().split('T')[0]);
    setPaymentMethod(EPaymentMethod.TRANSFER);
    setPaymentStatus(EPaymentStatus.COMPLETED);
    onOpenChange(false);
  };

  // Reset single fields when service changes
  useEffect(() => {
    setSingleAccountId(undefined);
    setSingleSlotId(undefined);
    setSinglePricePlanId(undefined);
  }, [singleServiceId]);

  // Reset slot when account or type changes
  useEffect(() => {
    setSingleSlotId(undefined);
  }, [singleAccountId, singleSubType]);

  // Reset price plan when sub type changes
  useEffect(() => {
    setSinglePricePlanId(undefined);
  }, [singleSubType]);

  // ─── Queries ─────────────────────────────────────────────────────────────────

  // All services (for single selector)
  const { data: servicesResp } = useQuery({
    queryKey: ['streaming-services-all'],
    queryFn: () => streamingServiceApi.getAll({ page: 1, limit: 100 }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const allServices = servicesResp?.data || [];

  // Selected service full detail (price plans)
  const { data: singleServiceResp, isLoading: isLoadingSingleService } = useQuery({
    queryKey: ['streaming-service', singleServiceId],
    queryFn: () => streamingServiceApi.getById(singleServiceId!),
    enabled: open && !!singleServiceId,
  });
  const singleService = singleServiceResp?.data;

  // Active accounts for the selected single service
  const { data: singleAccountsResp, isLoading: isLoadingSingleAccounts } = useQuery({
    queryKey: ['streaming-accounts', 'by-service', singleServiceId, EStreamingAccountStatus.ACTIVE],
    queryFn: () =>
      streamingAccountService.getAll({
        serviceId: singleServiceId,
        status: EStreamingAccountStatus.ACTIVE,
        limit: 100,
      }),
    enabled: open && !!singleServiceId,
  });

  // All active combos
  const { data: combosResp } = useQuery({
    queryKey: ['combos-select'],
    queryFn: () => comboApi.getAll({ limit: 100, status: EComboStatus.ACTIVE }),
    enabled: open && subType === ESubType.COMBO,
    staleTime: 5 * 60 * 1000,
  });
  const allCombos = combosResp?.data || [];

  // Selected combo detail (to get its services)
  const { data: comboDetailResp, isLoading: isLoadingCombo } = useQuery({
    queryKey: ['combo', selectedComboId],
    queryFn: () => comboApi.getById(selectedComboId!),
    enabled: open && !!selectedComboId,
  });
  const comboDetail = comboDetailResp?.data;

  // Service IDs in the selected combo
  const comboServiceIds = useMemo(
    () => comboDetail?.comboServices?.map((cs) => cs.service.id) ?? [],
    [comboDetail],
  );

  // Load each service in combo in parallel (for price plans)
  const comboServiceQueries = useQueries({
    queries: comboServiceIds.map((serviceId) => ({
      queryKey: ['streaming-service', serviceId],
      queryFn: () => streamingServiceApi.getById(serviceId),
      enabled: open && !!serviceId,
      staleTime: 5 * 60 * 1000,
    })),
  });

  // Load active accounts for each service in the combo
  const comboAccountsQueries = useQueries({
    queries: comboServiceIds.map((serviceId) => ({
      queryKey: ['streaming-accounts', 'by-service', serviceId, EStreamingAccountStatus.ACTIVE],
      queryFn: () =>
        streamingAccountService.getAll({
          serviceId,
          status: EStreamingAccountStatus.ACTIVE,
          limit: 100,
        }),
      enabled: open && !!serviceId,
      staleTime: 5 * 60 * 1000,
    })),
  });

  // Initialize combo item states when combo detail loads
  useEffect(() => {
    if (!comboDetail?.comboServices?.length) return;
    const initial: Record<number, ComboItemState> = {};
    comboDetail.comboServices.forEach((cs) => {
      initial[cs.service.id] = {
        serviceId: cs.service.id,
        accountId: undefined,
        slotId: undefined,
        pricePlanId: undefined,
        subscriptionType: ESubscriptionType.SLOT,
      };
    });
    setComboItems(initial);
  }, [comboDetail]);

  // ─── Derived SINGLE values ────────────────────────────────────────────────────

  const singleAccounts = singleAccountsResp?.data ?? [];

  const singleAccount = singleAccounts.find((a) => a.id === singleAccountId);

  const singleAvailableSlots =
    singleAccount?.slots?.filter((s) => s.status === ESlotStatus.AVAILABLE) ?? [];

  const singlePricePlans =
    singleService?.pricePlans?.filter(
      (p) =>
        p.status === 'ACTIVE' &&
        p.pricePlanType ===
          (singleSubType === ESubscriptionType.SLOT ? 'SLOT' : 'FULL_ACCOUNT'),
    ) ?? [];

  const singlePricePlan = singlePricePlans.find((p) => p.id === singlePricePlanId);

  const singleEndDate = useMemo(() => {
    if (!singlePricePlan || !startDate) return null;
    const d = new Date(startDate);
    d.setDate(d.getDate() + singlePricePlan.duration);
    return d.toISOString().split('T')[0];
  }, [singlePricePlan, startDate]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const updateComboItem = (
    serviceId: number,
    field: keyof ComboItemState,
    value: number | ESubscriptionType | undefined,
  ) => {
    setComboItems((prev) => {
      const current = prev[serviceId] ?? {
        serviceId,
        accountId: undefined,
        slotId: undefined,
        pricePlanId: undefined,
        subscriptionType: ESubscriptionType.SLOT,
      };
      const updated = { ...current, [field]: value };
      if (field === 'accountId') updated.slotId = undefined;
      if (field === 'subscriptionType') {
        updated.slotId = undefined;
        updated.pricePlanId = undefined;
      }
      return { ...prev, [serviceId]: updated };
    });
  };

  // ─── Mutation ─────────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: CreateSubscriptionRequest) => subscriptionService.create(data),
    onSuccess: () => {
      toast.success('Suscripción creada exitosamente');
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['streaming-accounts'] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // ─── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (clientMode === 'existing' && !clientId) {
      toast.error('Selecciona un cliente');
      return;
    }
    if (clientMode === 'new' && (!newClientData.name.trim() || !newClientData.phone.trim())) {
      toast.error('Ingresa nombre y teléfono del nuevo cliente');
      return;
    }

    const clientPayload =
      clientMode === 'existing' ? { clientId } : { newClient: newClientData };

    if (subType === ESubType.SINGLE) {
      if (!singleServiceId || !singleAccountId || !singlePricePlanId) {
        toast.error('Completa servicio, cuenta y plan de precio');
        return;
      }
      if (singleSubType === ESubscriptionType.SLOT && !singleSlotId) {
        toast.error('Selecciona un slot disponible');
        return;
      }

      const item: SubscriptionItemRequest = {
        serviceId: Number(singleServiceId),
        streamingAccountId: Number(singleAccountId),
        subscriptionType: singleSubType,
        pricePlanId: Number(singlePricePlanId),
        ...(singleSubType === ESubscriptionType.SLOT && singleSlotId
          ? { slotId: Number(singleSlotId) }
          : {}),
      };

      createMutation.mutate({
        ...clientPayload,
        type: ESubType.SINGLE,
        startDate,
        paymentMethod,
        paymentStatus,
        subscriptionItems: [item],
      });
    } else {
      if (!selectedComboId) {
        toast.error('Selecciona un combo');
        return;
      }
      const items = Object.values(comboItems);
      if (!items.length) {
        toast.error('El combo no tiene servicios definidos');
        return;
      }
      const incomplete = items.some(
        (it) =>
          !it.accountId ||
          !it.pricePlanId ||
          (it.subscriptionType === ESubscriptionType.SLOT && !it.slotId),
      );
      if (incomplete) {
        toast.error('Completa cuenta, slot y plan para cada servicio del combo');
        return;
      }

      const subscriptionItems: SubscriptionItemRequest[] = items.map((it) => ({
        serviceId: Number(it.serviceId),
        streamingAccountId: Number(it.accountId),
        subscriptionType: it.subscriptionType,
        pricePlanId: Number(it.pricePlanId),
        ...(it.subscriptionType === ESubscriptionType.SLOT && it.slotId
          ? { slotId: Number(it.slotId) }
          : {}),
      }));

      createMutation.mutate({
        ...clientPayload,
        type: ESubType.COMBO,
        comboId: selectedComboId,
        startDate,
        paymentMethod,
        paymentStatus,
        subscriptionItems,
      });
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-1">
          <DialogTitle>Nueva Suscripción</DialogTitle>
          <DialogDescription>Crea una suscripción individual o en combo</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ── Tipo ── */}
          <Tabs value={subType} onValueChange={(v) => setSubType(v as ESubType)}>
            <TabsList className="w-full">
              <TabsTrigger value={ESubType.SINGLE} className="flex-1">
                Individual (Single)
              </TabsTrigger>
              <TabsTrigger value={ESubType.COMBO} className="flex-1 gap-1.5">
                <Package className="h-3.5 w-3.5" />
                Combo
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* ── Cliente ── */}
          <div className="space-y-2 border rounded-md p-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5 text-sm font-semibold">
                <User className="h-3.5 w-3.5" />
                Cliente
              </Label>
              <Tabs
                value={clientMode}
                onValueChange={(v) => setClientMode(v as 'existing' | 'new')}
              >
                <TabsList className="h-7">
                  <TabsTrigger value="existing" className="text-xs px-2 h-6 gap-1">
                    <User className="h-3 w-3" />
                    Existente
                  </TabsTrigger>
                  <TabsTrigger value="new" className="text-xs px-2 h-6 gap-1">
                    <UserPlus className="h-3 w-3" />
                    Nuevo
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {clientMode === 'existing' ? (
              <ClientCombobox
                value={selectedClient}
                onChange={(client) => {
                  setSelectedClient(client);
                  setClientId(client ? Number(client.id) : undefined);
                }}
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input
                  placeholder="Nombre"
                  className="h-9"
                  value={newClientData.name}
                  onChange={(e) =>
                    setNewClientData((p) => ({ ...p, name: e.target.value }))
                  }
                />
                <Input
                  placeholder="Teléfono"
                  className="h-9"
                  value={newClientData.phone}
                  onChange={(e) =>
                    setNewClientData((p) => ({ ...p, phone: e.target.value }))
                  }
                />
                <Select
                  value={newClientData.from}
                  onValueChange={(v) =>
                    setNewClientData((p) => ({ ...p, from: v as EfromClient }))
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EfromClient.WEB}>Web</SelectItem>
                    <SelectItem value={EfromClient.REFERRAL}>Referido</SelectItem>
                    <SelectItem value={EfromClient.FACEBOOK}>Facebook</SelectItem>
                    <SelectItem value={EfromClient.INSTAGRAM}>Instagram</SelectItem>
                    <SelectItem value={EfromClient.ADVERTISEMENT}>Publicidad</SelectItem>
                    <SelectItem value={EfromClient.OTHER}>Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* ── SINGLE ── */}
          {subType === ESubType.SINGLE && (
            <div className="space-y-3 border rounded-md p-3">
              <Label className="text-sm font-semibold block">Servicio y cuenta</Label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Servicio */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Servicio</Label>
                  <Select
                    value={singleServiceId?.toString()}
                    onValueChange={(v) => setSingleServiceId(Number(v))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Seleccionar servicio" />
                    </SelectTrigger>
                    <SelectContent>
                      {allServices.map((s) => (
                        <SelectItem key={s.id} value={s.id.toString()}>
                          {s.icon} {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tipo de suscripción */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Tipo</Label>
                  <Select
                    value={singleSubType}
                    onValueChange={(v) => setSingleSubType(v as ESubscriptionType)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ESubscriptionType.SLOT}>Slot</SelectItem>
                      <SelectItem value={ESubscriptionType.FULL_ACCOUNT}>
                        Cuenta completa
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(isLoadingSingleService || isLoadingSingleAccounts) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando cuentas...
                </div>
              )}

              {singleService && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Cuenta */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Cuenta</Label>
                    <AccountCombobox
                      accounts={singleAccounts}
                      value={singleAccountId}
                      onChange={(id) => setSingleAccountId(id)}
                    />
                  </div>

                  {/* Slot (solo si tipo = SLOT) */}
                  {singleSubType === ESubscriptionType.SLOT && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Slot</Label>
                      <Select
                        value={singleSlotId?.toString()}
                        onValueChange={(v) => setSingleSlotId(Number(v))}
                        disabled={!singleAccountId}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Seleccionar slot" />
                        </SelectTrigger>
                        <SelectContent>
                          {singleAvailableSlots.length === 0 ? (
                            <div className="p-2 text-sm text-muted-foreground text-center">
                              Sin slots disponibles
                            </div>
                          ) : (
                            singleAvailableSlots.map((slot) => (
                              <SelectItem key={slot.id} value={slot.id.toString()}>
                                {slot.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Plan de precio */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Plan de precio</Label>
                    <Select
                      value={singlePricePlanId?.toString()}
                      onValueChange={(v) => setSinglePricePlanId(Number(v))}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Seleccionar plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {singlePricePlans.length === 0 ? (
                          <div className="p-2 text-sm text-muted-foreground text-center">
                            Sin planes disponibles
                          </div>
                        ) : (
                          singlePricePlans.map((plan) => (
                            <SelectItem key={plan.id} value={plan.id.toString()}>
                              ${formatCurrency(plan.price)} / {plan.duration}d — {plan.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── COMBO ── */}
          {subType === ESubType.COMBO && (
            <div className="space-y-3 border rounded-md p-3">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-primary" />
                <Label className="text-sm font-semibold">Combo</Label>
              </div>

              <Select
                value={selectedComboId?.toString()}
                onValueChange={(v) => setSelectedComboId(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar combo" />
                </SelectTrigger>
                <SelectContent>
                  {allCombos.map((combo) => (
                    <SelectItem key={combo.id} value={combo.id.toString()}>
                      {combo.name} — ${formatCurrency(combo.price)} / {combo.duration}d
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isLoadingCombo && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando combo...
                </div>
              )}

              {/* Asignación por servicio */}
              {comboDetail?.comboServices && comboDetail.comboServices.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                    Asignar cuenta por servicio
                  </p>
                  {comboDetail.comboServices.map((cs, index) => {
                    const serviceQuery = comboServiceQueries[index];
                    const serviceData = serviceQuery?.data?.data;
                    const accountsQuery = comboAccountsQueries[index];
                    const item = comboItems[cs.service.id] ?? {
                      serviceId: cs.service.id,
                      accountId: undefined,
                      slotId: undefined,
                      pricePlanId: undefined,
                      subscriptionType: ESubscriptionType.SLOT,
                    };

                    const accounts = accountsQuery?.data?.data ?? [];
                    const selectedAcc = accounts.find((a) => a.id === item.accountId);
                    const availableSlots =
                      selectedAcc?.slots?.filter((s) => s.status === ESlotStatus.AVAILABLE) ?? [];
                    const pricePlans =
                      serviceData?.pricePlans?.filter(
                        (p) =>
                          p.status === 'ACTIVE' &&
                          p.pricePlanType ===
                            (item.subscriptionType === ESubscriptionType.SLOT
                              ? 'SLOT'
                              : 'FULL_ACCOUNT'),
                      ) ?? [];

                    return (
                      <div
                        key={cs.service.id}
                        className="border rounded-md p-3 space-y-2 bg-muted/20"
                      >
                        {/* Service header */}
                        <div className="flex items-center gap-2">
                          <span
                            className="w-7 h-7 rounded-md flex items-center justify-center text-base flex-shrink-0"
                            style={{
                              backgroundColor: cs.service?.color
                                ? `${cs.service.color}20`
                                : 'hsl(var(--muted))',
                            }}
                          >
                            {cs.service.icon}
                          </span>
                          <span className="font-medium text-sm">{cs.service.name}</span>
                          {(serviceQuery?.isLoading || accountsQuery?.isLoading) && (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                          )}
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {/* Tipo */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Tipo</Label>
                            <Select
                              value={item.subscriptionType}
                              onValueChange={(v) =>
                                updateComboItem(
                                  cs.service.id,
                                  'subscriptionType',
                                  v as ESubscriptionType,
                                )
                              }
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={ESubscriptionType.SLOT}>Slot</SelectItem>
                                <SelectItem value={ESubscriptionType.FULL_ACCOUNT}>
                                  Cuenta completa
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Cuenta */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Cuenta</Label>
                            <AccountCombobox
                              accounts={accounts}
                              value={item.accountId}
                              onChange={(id) =>
                                updateComboItem(cs.service.id, 'accountId', id)
                              }
                              disabled={!accountsQuery?.data}
                              size="sm"
                            />
                          </div>

                          {/* Slot (solo si tipo = SLOT) */}
                          {item.subscriptionType === ESubscriptionType.SLOT && (
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Slot</Label>
                              <Select
                                value={item.slotId?.toString()}
                                onValueChange={(v) =>
                                  updateComboItem(cs.service.id, 'slotId', Number(v))
                                }
                                disabled={!item.accountId}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue placeholder="Slot" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableSlots.length === 0 ? (
                                    <div className="p-2 text-xs text-muted-foreground text-center">
                                      Sin slots
                                    </div>
                                  ) : (
                                    availableSlots.map((slot) => (
                                      <SelectItem
                                        key={slot.id}
                                        value={slot.id.toString()}
                                        className="text-xs"
                                      >
                                        {slot.name}
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Plan de precio */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Plan</Label>
                            <Select
                              value={item.pricePlanId?.toString()}
                              onValueChange={(v) =>
                                updateComboItem(cs.service.id, 'pricePlanId', Number(v))
                              }
                              disabled={!serviceData}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Plan" />
                              </SelectTrigger>
                              <SelectContent>
                                {pricePlans.length === 0 ? (
                                  <div className="p-2 text-xs text-muted-foreground text-center">
                                    Sin planes
                                  </div>
                                ) : (
                                  pricePlans.map((plan) => (
                                    <SelectItem
                                      key={plan.id}
                                      value={plan.id.toString()}
                                      className="text-xs"
                                    >
                                      ${Number(plan.price).toFixed(0)}/{plan.duration}d
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Pago y fecha ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1 text-muted-foreground">
                <Calendar className="h-3 w-3" />
                Fecha inicio
              </Label>
              <Input
                type="date"
                className="h-9"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1 text-muted-foreground">
                <Wallet className="h-3 w-3" />
                Método de pago
              </Label>
              <Select
                value={paymentMethod}
                onValueChange={(v) => setPaymentMethod(v as EPaymentMethod)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EPaymentMethod.TRANSFER}>Transferencia</SelectItem>
                  <SelectItem value={EPaymentMethod.CASH}>Efectivo</SelectItem>
                  <SelectItem value={EPaymentMethod.NEQUI}>Nequi</SelectItem>
                  <SelectItem value={EPaymentMethod.DAVIPLATA}>Daviplata</SelectItem>
                  <SelectItem value={EPaymentMethod.BANCOLOMBIA}>Bancolombia</SelectItem>
                  <SelectItem value={EPaymentMethod.PAYPAL}>Paypal</SelectItem>
                  <SelectItem value={EPaymentMethod.BINANCE}>Binance</SelectItem>
                  <SelectItem value={EPaymentMethod.YAPE}>Yape</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1 text-muted-foreground">
                <CircleCheck className="h-3 w-3" />
                Estado de pago
              </Label>
              <Select
                value={paymentStatus}
                onValueChange={(v) => setPaymentStatus(v as EPaymentStatus)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EPaymentStatus.COMPLETED}>Completado</SelectItem>
                  <SelectItem value={EPaymentStatus.PENDING}>Pendiente</SelectItem>
                  <SelectItem value={EPaymentStatus.CANCELLED}>Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Resumen + footer ── */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t">
            {/* Summary */}
            {subType === ESubType.SINGLE && singlePricePlan ? (
              <div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm px-2 py-1.5 bg-primary/5 rounded-md">
                <span className="text-muted-foreground">
                  Precio:{' '}
                  <strong className="text-foreground">
                    ${Number(singlePricePlan.price).toFixed(0)}
                  </strong>
                </span>
                <span className="text-muted-foreground">
                  Duración:{' '}
                  <strong className="text-foreground">{singlePricePlan.duration}d</strong>
                </span>
                {singleEndDate && (
                  <span className="text-muted-foreground">
                    Fin: <strong className="text-foreground">{singleEndDate}</strong>
                  </span>
                )}
              </div>
            ) : subType === ESubType.COMBO && comboDetail ? (
              <div className="flex-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm px-2 py-1.5 bg-primary/5 rounded-md">
                <span className="text-muted-foreground">
                  Combo: <strong className="text-foreground">{comboDetail.name}</strong>
                </span>
                <span className="text-muted-foreground">
                  Precio:{' '}
                  <strong className="text-foreground">${formatCurrency(comboDetail.price)}</strong>
                </span>
                <span className="text-muted-foreground">
                  Duración:{' '}
                  <strong className="text-foreground">{comboDetail.duration}d</strong>
                </span>
              </div>
            ) : (
              <div className="flex-1" />
            )}

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClose}
                disabled={createMutation.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" size="sm" disabled={createMutation.isPending}>
                {createMutation.isPending && (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                )}
                Crear Suscripción
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
